import { RadioGroup as RadioPrimitive } from 'radix-ui';
import { forwardRef, type ComponentPropsWithoutRef, type ReactNode } from 'react';

import { cn } from '../cn';

export const RadioGroup = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<typeof RadioPrimitive.Root>>(function RadioGroup(
  { className, ...props },
  ref,
) {
  return <RadioPrimitive.Root ref={ref} className={cn('flex flex-col gap-2', className)} {...props} />;
});

export interface RadioItemProps extends ComponentPropsWithoutRef<typeof RadioPrimitive.Item> {
  label: ReactNode;
  description?: ReactNode;
}

export const RadioItem = forwardRef<HTMLButtonElement, RadioItemProps>(function RadioItem(
  { className, label, description, id, ...props },
  ref,
) {
  const itemId = id ?? `radio-${props.value}`;
  return (
    <div className={cn('flex items-start gap-2', className)}>
      <RadioPrimitive.Item
        ref={ref}
        id={itemId}
        className="mt-0.5 inline-flex size-4 shrink-0 items-center justify-center rounded-full border border-border-strong bg-surface hover:border-ink-muted data-[state=checked]:border-primary disabled:opacity-50"
        {...props}
      >
        <RadioPrimitive.Indicator className="size-2 rounded-full bg-primary" />
      </RadioPrimitive.Item>
      <label htmlFor={itemId} className="flex flex-col">
        <span className="text-body text-ink">{label}</span>
        {description ? <span className="text-meta text-ink-muted">{description}</span> : null}
      </label>
    </div>
  );
});

/** Segmented choice for 2–5 short options (e.g. product PF / CC / HF / SME). */
export const SegmentedRadio = forwardRef<
  HTMLDivElement,
  ComponentPropsWithoutRef<typeof RadioPrimitive.Root> & { options: readonly { value: string; label: ReactNode }[] }
>(function SegmentedRadio({ className, options, ...props }, ref) {
  return (
    <RadioPrimitive.Root
      ref={ref}
      orientation="horizontal"
      className={cn('inline-flex rounded-sm border border-border-strong bg-surface p-0.5', className)}
      {...props}
    >
      {options.map((o) => (
        <RadioPrimitive.Item
          key={o.value}
          value={o.value}
          className="h-7 rounded-sm px-3 text-label text-ink-secondary hover:text-ink data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground"
        >
          {o.label}
        </RadioPrimitive.Item>
      ))}
    </RadioPrimitive.Root>
  );
});
